/**
 * Las 7 piezas de «Armá tu nave» (encargo 117): una por parte de speak. El orden es el de los lugares
 * del anillo y el de la «pieza de hoy» (la primera que falta). Qué cuenta como hecha y adónde lleva cada
 * una lo decide el backend (`GET /api/modules/english/ship`); acá viven sólo los textos y el dibujo.
 * Los ids son el contrato con el backend (`SHIP_PIECE_IDS` en el módulo languages).
 */
export type ShipMedium =
  | 'charla'
  | 'escuchar'
  | 'leccion'
  | 'escena'
  | 'palabras'
  | 'juego'
  | 'semana';
export type ShipPieceId = 'cabina' | 'antena' | 'rumbo' | 'tren' | 'bodega' | 'motores' | 'radar';

export interface ShipPiece {
  id: ShipPieceId;
  /** Nombre de la pieza de la nave. */
  name: string;
  /** Versión corta, para las fichas angostas. */
  short: string;
  medium: ShipMedium;
  /** La parte de speak que representa. */
  feature: string;
  /** Título de la tarjeta «pieza de hoy». */
  title: string;
  /** Para qué sirve, en el cartel de «¡Pieza nueva!». */
  purpose: string;
  /** Qué hacer, en la tarjeta «pieza de hoy». */
  task: string;
  /** El botón de la tarjeta «pieza de hoy». */
  cta: string;
}

export const SHIP_PIECES: readonly ShipPiece[] = [
  {
    id: 'cabina',
    name: 'Cabina',
    short: 'Cabina',
    medium: 'charla',
    feature: 'Charla con el coach',
    title: 'Una charla para conocerte',
    purpose: 'Desde la cabina hablás con tu copiloto: el coach.',
    task: 'Contale al coach quién sos y para qué querés el inglés.',
    cta: 'Empezar la charla',
  },
  {
    id: 'antena',
    name: 'Antena',
    short: 'Antena',
    medium: 'escuchar',
    feature: 'Audioteca',
    title: 'Escuchá una historia',
    purpose: 'Con la antena escuchás historias en inglés.',
    task: 'Escuchá 2 minutos de una historia: que la elija la app según lo que te interesa, o elegila vos.',
    cta: 'Que la elija la app',
  },
  {
    id: 'rumbo',
    name: 'Computadora de rumbo',
    short: 'Rumbo',
    medium: 'leccion',
    feature: 'Lección del Programa',
    title: 'Tu lección',
    purpose: 'Marca el rumbo: las lecciones del Programa.',
    task: 'Completá la lección que te toca en el Programa: teoría corta y práctica hablada.',
    cta: 'Empezar la lección',
  },
  {
    id: 'tren',
    name: 'Tren de aterrizaje',
    short: 'Tren',
    medium: 'escena',
    feature: 'Escena',
    title: 'Una situación real',
    purpose: 'Para bajar a situaciones reales.',
    task: 'Cumplí la misión de una escena de la vida real con el coach.',
    cta: 'Empezar la escena',
  },
  {
    id: 'bodega',
    name: 'Bodega',
    short: 'Bodega',
    medium: 'palabras',
    feature: 'Biblioteca',
    title: 'Tus palabras',
    purpose: 'Donde guardás las palabras que querés aprender.',
    // Para qué sirve guardar, antes de pedirlo (Emmanuel, 20/10): quedan en «Lo mío» y, al guardar la
    // tercera, «Decilas ahora» las pide en voz alta, cada una en su frase de ejemplo.
    task: 'Guardá 3 palabras que quieras aprender. Quedan en «Lo mío» de tu Biblioteca, y ahora mismo las decís en una frase para empezar a usarlas.',
    cta: 'Ver mis palabras',
  },
  {
    id: 'motores',
    name: 'Motores',
    short: 'Motores',
    medium: 'juego',
    feature: 'Juego',
    title: 'Un juego',
    purpose: 'El impulso para despegar: practicar jugando.',
    task: 'Contestá 3 rondas de «Decilo en inglés».',
    cta: 'Jugar',
  },
  {
    id: 'radar',
    name: 'Radar',
    short: 'Radar',
    medium: 'semana',
    feature: 'Progreso',
    title: 'Mirá cómo vas',
    purpose: 'Te muestra cómo vas y en qué conviene enfocarte.',
    task: 'Mirá en Progreso lo que charlaste y en qué enfocarte.',
    cta: 'Ver mi progreso',
  },
];

export const pieceById = (id: ShipPieceId): ShipPiece => {
  const p = SHIP_PIECES.find((x) => x.id === id);
  if (!p) throw new Error(`pieza de la nave desconocida: ${id}`);
  return p;
};
